import { useEffect, useRef, useState } from "react";
import { BlurFade } from "@/components/ui/blur-fade";

/* ----------------------------------------------------------------------------
 * "Commercials we made with AI" — the AI Video Workshop's sample reel.
 *
 * THE LIST IS DAVE'S, NOT OURS. These five are the spots Dave Spector cleared
 * for the site (Slack to Esther, 2026-10-26). Every one lives on AdVenture
 * Media's own YouTube channel. Do not add a commercial here, or a still from
 * one anywhere else on the page, without his say-so: some client work in
 * client/public/videos/creative-work/ is not cleared for this page.
 *
 * NOTHING LOADS FROM YOUTUBE UNTIL SOMEONE PRESSES PLAY. Each card is a poster
 * button (YouTube's own frame, self-hosted at three widths) that swaps itself
 * for a youtube-nocookie player on click. Five live iframes
 * would cost ~5MB of player script before anyone watched a second.
 *
 * THE CARDS MOVE (Isaac, 2026-10-28: "more videos, more gifs"). A muted 9-10s
 * cut of each spot loops over its poster while the card is on screen. Three
 * are cut from client/public/videos/creative-work/; Glitter Stix and Augusta
 * from their YouTube uploads on AdVenture's channel.
 *
 * Layout: two up, then three up, on a 6-column grid. Every card is 16:9 (all
 * five are, per YouTube's oEmbed), so nothing is cropped.
 * -------------------------------------------------------------------------- */

interface SampleWork {
  youtube: string;
  /** Poster file stem in /images/ai-video-workshop/work/. */
  poster: string;
  client: string;
  /** Omitted where the spot has no name of its own. */
  title?: string;
  /** Muted preview loop, cut from the spot itself. */
  preview?: string;
}

/* Posters are the videos' own YouTube frames, self-hosted at three widths
   (2026-10-26): the maxres JPEGs were 90 to 150KB each and a phone loaded all
   five at 1280px before anyone scrolled. Now ~20KB each at phone size. */
const POSTER_DIR = "/images/ai-video-workshop/work";
const PREVIEW_DIR = "/videos/ai-video-workshop/work";

const SAMPLE_WORK: SampleWork[] = [
  { youtube: "sxRw7TVTIgE", poster: "glitter-stix", client: "Glitter Stix", title: "Musical", preview: "glitter-stix" },
  { youtube: "7DWhV1xLgNI", poster: "mitogrow", client: "MitoGrow", title: "We're Not Dead Yet", preview: "mitogrow" },
  { youtube: "lG5-BRgjz3c", poster: "exotic-nutrition", client: "Exotic Nutrition", title: "Podcast", preview: "exotic-nutrition" },
  { youtube: "qdZ1_Ehg_F4", poster: "happsy", client: "Happsy", preview: "happsy" },
  { youtube: "1EMTymglk3A", poster: "augusta-lawn-care", client: "Augusta Lawn Care", title: "Christmas Lights", preview: "augusta-lawn-care" },
];

/* Same contract as the build cards' loop: no bytes until within 250px of the
   viewport, paused off screen, and never under reduced motion (the poster
   stays). */
function PreviewLoop({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const load = () => { if (!v.src) v.src = src; v.play().catch(() => {}); };
    if (!("IntersectionObserver" in window)) { load(); return; }
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) load(); else if (v.src) v.pause(); },
      { rootMargin: "250px 0px" },
    );
    io.observe(v);
    return () => io.disconnect();
  }, [src]);
  return <video ref={ref} className="avw-work-loop" muted loop playsInline preload="none" aria-hidden="true" />;
}

function SampleCard({ work, index }: { work: SampleWork; index: number }) {
  const [playing, setPlaying] = useState(false);
  const label = work.title ? `${work.client}, ${work.title}` : work.client;
  return (
    <figure className={`avw-work-card avw-work-card-${index < 2 ? "wide" : "narrow"}`}>
      <div className="avw-work-frame">
        {playing ? (
          <iframe
            className="avw-work-player"
            src={`https://www.youtube-nocookie.com/embed/${work.youtube}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={label}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="avw-work-poster"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${label}`}
            data-testid={`sample-work-${work.youtube}`}
          >
            <img
              src={`${POSTER_DIR}/${work.poster}-800.webp`}
              srcSet={`${POSTER_DIR}/${work.poster}-480.webp 480w, ${POSTER_DIR}/${work.poster}-800.webp 800w, ${POSTER_DIR}/${work.poster}-1200.webp 1200w`}
              sizes={index < 2 ? "(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) 46vw, 630px" : "(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) 46vw, 413px"}
              alt=""
              width={1280}
              height={720}
              loading="lazy"
              decoding="async"
            />
            {work.preview && <PreviewLoop src={`${PREVIEW_DIR}/${work.preview}.mp4`} />}
            <span className="avw-work-play" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
              </svg>
            </span>
          </button>
        )}
      </div>
      {/* No caption (Esther, 2026-10-26): the work speaks for itself. The
          client and title live on in the button's aria-label and the
          player's title. */}
    </figure>
  );
}

export function SampleWorkSection() {
  return (
    <section aria-labelledby="sample-work-heading" id="sample-work" className="avw-work">
      <style>{`
        /* No bottom padding: #learn below brings its own top. */
        .avw-work { padding: clamp(88px, 9vw, 130px) 0 0; }
        .avw-work-h {
          font-family: var(--font-display);
          font-weight: 400;
          font-size: clamp(38px, 5.2vw, 62px);
          line-height: 1.12;
          letter-spacing: -0.035em;
          margin: 0;
          text-wrap: balance;
        }
        .avw-work-sub {
          font-family: var(--font-sans);
          font-size: clamp(17px, 1.4vw, 20px);
          line-height: 1.55;
          color: var(--charcoal-muted, rgba(29, 29, 31, 0.72));
          max-width: 640px;
          margin: 20px 0 0;
          text-wrap: pretty;
        }
        .avw-work-grid {
          margin-top: 48px;
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 20px;
        }
        .avw-work-card { margin: 0; min-width: 0; }
        .avw-work-card-wide   { grid-column: span 3; }
        .avw-work-card-narrow { grid-column: span 2; }
        .avw-work-frame {
          position: relative;
          aspect-ratio: 16 / 9;
          border-radius: 10px;
          overflow: hidden;
          /* Only ever seen in the instant before a poster paints. */
          background: #1D1D1F;
        }
        .avw-work-poster {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          padding: 0;
          border: 0;
          cursor: pointer;
          background: none;
        }
        .avw-work-poster img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 600ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .avw-work-loop {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 600ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .avw-work-poster:hover img,
        .avw-work-poster:focus-visible img,
        .avw-work-poster:hover .avw-work-loop,
        .avw-work-poster:focus-visible .avw-work-loop { transform: scale(1.03); }
        .avw-work-poster:focus-visible { outline: 2px solid var(--pg-chrome, #9B0032); outline-offset: 3px; }
        .avw-work-play {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 64px;
          height: 64px;
          margin: -32px 0 0 -32px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          padding-left: 3px;
          background: var(--pg-chrome, #9B0032);
          color: var(--pg-chrome-ink, #F3BDAF);
          transition: transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .avw-work-poster:hover .avw-work-play,
        .avw-work-poster:focus-visible .avw-work-play { transform: scale(1.08); }
        .avw-work-player { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
        @media (max-width: 1023px) {
          .avw-work-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .avw-work-card-wide,
          .avw-work-card-narrow { grid-column: span 1; }
          .avw-work-card:last-child { grid-column: 1 / -1; }
        }
        @media (max-width: 767px) {
          .avw-work-grid { grid-template-columns: 1fr; gap: 28px; }
          .avw-work-play { width: 52px; height: 52px; margin: -26px 0 0 -26px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .avw-work-poster img, .avw-work-loop, .avw-work-play { transition: none; }
        }
      `}</style>

      <BlurFade inView delay={0} duration={0.5} yOffset={12}>
        <h2 id="sample-work-heading" className="avw-work-h">
          Commercials we made with AI.
        </h2>
        <p className="avw-work-sub">
          In the workshop, Dave shows you how we made commercials like these, including everything that went wrong along the way.
        </p>
      </BlurFade>

      <div className="avw-work-grid">
        {SAMPLE_WORK.map((w, i) => (
          <SampleCard key={w.youtube} work={w} index={i} />
        ))}
      </div>
    </section>
  );
}
